import { cloneHeadersWithAuth } from '@app/utils/api';

interface SseOptions {
  headers?: HeadersInit;
  getUrl?: () => string;
  sendLastEventId?: boolean;
  onOpen?: () => void;
  onEvent: (type: string, data: string, id?: string) => void;
  reconnect?: {
    enabled?: boolean;
    maxAttempts?: number;
    retryDelayMs?: number;
  };
}

const EVENT_SEPARATOR = /\r?\n\r?\n/;
const reconnectExhaustedError = (url: string) => new Error(`SSE reconnect attempts exhausted for ${url}`);

interface ParsedSseEvent {
  type: string;
  data: string;
  id?: string;
  clearId?: boolean;
}

const parseSseEvent = (rawEvent: string) => {
  const lines = rawEvent.split(/\r?\n/);
  let eventType = 'message';
  let eventId: string | undefined;
  let clearId = false;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(':')) {
      continue;
    }
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }
    const field = line.slice(0, colonIndex).trim();
    let value = line.slice(colonIndex + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    if (field === 'event' && value) {
      eventType = value;
    } else if (field === 'id') {
      if (value === '') {
        eventId = undefined;
        clearId = true;
      } else {
        eventId = value;
        clearId = false;
      }
    } else if (field === 'data') {
      dataLines.push(value);
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  const parsed: ParsedSseEvent = { type: eventType, data: dataLines.join('\n') };
  if (eventId !== undefined) {
    parsed.id = eventId;
  }
  if (clearId) {
    parsed.clearId = true;
  }
  return parsed;
};

export const streamSse = async (url: string, options: SseOptions, signal?: AbortSignal): Promise<void> => {
  const reconnectEnabled = options.reconnect?.enabled === true;
  const maxAttempts = Math.max(0, options.reconnect?.maxAttempts ?? 2);
  const retryDelayMs = Math.max(0, options.reconnect?.retryDelayMs ?? 1000);
  let consecutiveFailures = 0;
  let lastEventId: string | undefined;

  const sleep = (ms: number) => new Promise((resolve) => globalThis.setTimeout(resolve, ms));

  while (true) {
    const requestUrl = options.getUrl?.() ?? url;
    const headerValues = await cloneHeadersWithAuth(options.headers);
    const headers = new Headers(headerValues);
    if (!headers.has('Accept')) {
      headers.set('Accept', 'text/event-stream');
    }
    if (options.sendLastEventId !== false && lastEventId && !headers.has('Last-Event-ID')) {
      headers.set('Last-Event-ID', lastEventId);
    }

    let response: Response;
    try {
      response = await fetch(requestUrl, {
        method: 'GET',
        headers,
        signal
      });
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      if (!reconnectEnabled || consecutiveFailures >= maxAttempts) {
        console.error('[sse] stream error', { url: requestUrl, error });
        throw error;
      }
      consecutiveFailures += 1;
      await sleep(retryDelayMs);
      continue;
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type') ?? '';
      const retriableStatus = response.status >= 500 || response.status === 429;
      if (reconnectEnabled && retriableStatus && consecutiveFailures < maxAttempts) {
        console.warn('[sse] connect failed; retrying', {
          url: requestUrl,
          status: response.status,
          statusText: response.statusText,
          contentType,
          hasAuth: headers.has('Authorization') || headers.has('authorization')
        });
        consecutiveFailures += 1;
        await sleep(retryDelayMs);
        continue;
      }
      console.error('[sse] connect failed', {
        url: requestUrl,
        status: response.status,
        statusText: response.statusText,
        contentType,
        hasAuth: headers.has('Authorization') || headers.has('authorization')
      });
      throw new Error(`Failed to connect to ${requestUrl}: ${response.status}`);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('text/event-stream')) {
      console.warn('[sse] unexpected content-type', { url: requestUrl, contentType });
    }

    options.onOpen?.();
    const reader = response.body?.getReader();
    if (!reader) {
      if (!reconnectEnabled) {
        return;
      }
      if (consecutiveFailures >= maxAttempts) {
        throw reconnectExhaustedError(requestUrl);
      }
      consecutiveFailures += 1;
      await sleep(retryDelayMs);
      continue;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let observedTraffic = false;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        if (value) {
          observedTraffic = true;
          buffer += decoder.decode(value, { stream: true });
        }

        let match = buffer.match(EVENT_SEPARATOR);
        while (match && typeof match.index === 'number') {
          const rawEvent = buffer.slice(0, match.index);
          buffer = buffer.slice(match.index + match[0].length);
          const parsed = parseSseEvent(rawEvent);
          if (parsed) {
            if (parsed.clearId) {
              lastEventId = undefined;
            } else if (parsed.id !== undefined) {
              lastEventId = parsed.id;
            }
            options.onEvent(parsed.type, parsed.data, parsed.id);
          }
          match = buffer.match(EVENT_SEPARATOR);
        }
      }

      if (buffer.trim()) {
        const parsed = parseSseEvent(buffer);
        if (parsed) {
          observedTraffic = true;
          if (parsed.clearId) {
            lastEventId = undefined;
          } else if (parsed.id !== undefined) {
            lastEventId = parsed.id;
          }
          options.onEvent(parsed.type, parsed.data, parsed.id);
        }
      }
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      console.error('[sse] stream error', { url: requestUrl, error });
      if (observedTraffic) {
        consecutiveFailures = 0;
      }
      if (!reconnectEnabled || consecutiveFailures >= maxAttempts) {
        throw error;
      }
      consecutiveFailures += 1;
      await sleep(retryDelayMs);
      continue;
    } finally {
      reader.releaseLock?.();
    }

    if (!reconnectEnabled || signal?.aborted) {
      return;
    }
    if (observedTraffic) {
      consecutiveFailures = 0;
    } else {
      if (consecutiveFailures >= maxAttempts) {
        throw reconnectExhaustedError(requestUrl);
      }
      consecutiveFailures += 1;
    }
    await sleep(retryDelayMs);
  }
};
